import {
  assertValidCrmChannelEmails,
  contactChannelsToApiBody,
  type CrmContactChannelsState,
} from "@/lib/crmContactChannels";
import {
  assertValidCrmAddresses,
  legacyCityStateFromAddresses,
} from "@/lib/crmContactAddresses";
import { crmAccountIsConverted } from "@/lib/crmDisplay";
import {
  CRM_LEAD_CHANNELS_SOURCE_DETAIL_MARKER,
  type LeadSourceDetailChannelOverflow,
} from "@/lib/crmLeadSourceDetailChannels";

export type LeadEntryKind = "contact" | "account";

export type LeadAccountAttachMode = "none" | "existing" | "new";

export type LeadAttachedContactDraft = {
  firstName: string;
  lastName: string;
  title: string;
  channels: CrmContactChannelsState;
};

export type CreateLeadFormSnapshot = {
  leadEntry: LeadEntryKind;
  source: string;
  stage: string;
  status: string;
  /** Cognito/user id; defaults to session user when omitted. */
  ownerId: string;
  contact: {
    firstName: string;
    lastName: string;
    title: string;
    channels: CrmContactChannelsState;
  };
  company: {
    name: string;
    domain: string;
    website: string;
    industry: string;
    industrySegment: string;
    channels: CrmContactChannelsState;
  };
  attach: {
    mode: LeadAccountAttachMode;
    existingAccountId: string;
    existingAccountName: string;
    existingAccountDomain: string;
    /** From GET /api/crm/accounts; `converted` means CRM Account, not Lead Account. */
    existingAccountStatus?: string | null;
  };
  /** Optional people to attach when lead entry is Lead Account. */
  attachedContacts: LeadAttachedContactDraft[];
};

type ChannelOverflow = LeadSourceDetailChannelOverflow;

function hasExtraChannels(
  channels: CrmContactChannelsState,
  legacy: { email: string | null; phone: string | null; linkedinUrl: string | null },
): boolean {
  const filledEmails = channels.emails.filter((e) => e.address.trim());
  const filledPhones = channels.phones.filter((p) => p.number.trim());
  const filledSocials = channels.socials.filter((s) => s.url.trim());
  if (filledEmails.length > 1) return true;
  if (filledPhones.length > 1) return true;
  if (filledSocials.length > 1) return true;
  if (filledPhones.some((p) => p.smsOptIn)) return true;
  if (filledSocials.some((s) => s.platform !== "linkedin")) return true;
  if (filledEmails.length === 1 && legacy.email && filledEmails[0].address !== legacy.email) return true;
  return false;
}

function buildSourceDetail(overflow: ChannelOverflow | null): string | undefined {
  if (!overflow) return undefined;
  return `${CRM_LEAD_CHANNELS_SOURCE_DETAIL_MARKER}${JSON.stringify(overflow)}`;
}

export function contactDraftToApiBody(draft: LeadAttachedContactDraft): Record<string, unknown> {
  const contactChannels = contactChannelsToApiBody(draft.channels);
  return {
    firstName: draft.firstName.trim() || undefined,
    lastName: draft.lastName.trim() || undefined,
    title: draft.title.trim() || undefined,
    email: contactChannels.email || undefined,
    phone: contactChannels.phone || undefined,
    linkedinUrl: contactChannels.linkedinUrl || undefined,
    emails: contactChannels.emails,
    phones: contactChannels.phones,
    socials: contactChannels.socials,
    addresses: contactChannels.addresses,
  };
}

function companyLocationFromChannels(channels: CrmContactChannelsState): { city?: string; state?: string } {
  const fromAddresses = legacyCityStateFromAddresses(contactChannelsToApiBody(channels).addresses);
  return {
    city: fromAddresses.city || undefined,
    state: fromAddresses.state || undefined,
  };
}

export function assertContactDraftValid(draft: LeadAttachedContactDraft, label: string): void {
  const contactChannels = contactChannelsToApiBody(draft.channels);
  const hasName = Boolean(
    [draft.firstName.trim(), draft.lastName.trim()].filter(Boolean).join(" ").trim(),
  );
  const hasIdentifier = Boolean(contactChannels.email || contactChannels.linkedinUrl);
  if (!hasName || !hasIdentifier) {
    throw new Error(
      `${label} needs a name plus at least one email or social profile (e.g. LinkedIn), or remove it.`,
    );
  }
}

export const LEAD_CONTACT_TO_CRM_CONTACT_NOTICE =
  "Because you selected an Account (not a Lead Account), this will be created as a Contact under that Account.";

/** Lead Contact + link to converted CRM Account → create Contact, not Lead Contact. */
export function leadContactCreateUsesCrmContact(form: CreateLeadFormSnapshot): boolean {
  return (
    form.leadEntry === "contact" &&
    form.attach.mode === "existing" &&
    Boolean(form.attach.existingAccountId) &&
    crmAccountIsConverted(form.attach.existingAccountStatus)
  );
}

export function buildCreateContactApiBodyFromLeadContactForm(
  form: CreateLeadFormSnapshot,
): Record<string, unknown> {
  if (!leadContactCreateUsesCrmContact(form)) {
    throw new Error("Contact create requires a converted CRM Account selection.");
  }
  assertValidCrmChannelEmails(form.contact.channels, "Lead Contact email");
  assertValidCrmAddresses(form.contact.channels.addresses, "Lead Contact address");
  const contactChannels = contactChannelsToApiBody(form.contact.channels);
  const hasName = Boolean(
    [form.contact.firstName.trim(), form.contact.lastName.trim()].filter(Boolean).join(" ").trim(),
  );
  const hasIdentifier = Boolean(contactChannels.email || contactChannels.linkedinUrl);
  if (!hasName || !hasIdentifier) {
    throw new Error("Lead Contact needs a name plus at least one email or social profile (e.g. LinkedIn).");
  }
  return {
    accountId: form.attach.existingAccountId,
    kind: "person",
    firstName: form.contact.firstName.trim() || undefined,
    lastName: form.contact.lastName.trim() || undefined,
    title: form.contact.title.trim() || undefined,
    email: contactChannels.email || undefined,
    phone: contactChannels.phone || undefined,
    linkedinUrl: contactChannels.linkedinUrl || undefined,
    emails: contactChannels.emails,
    phones: contactChannels.phones,
    socials: contactChannels.socials,
    addresses: contactChannels.addresses,
  };
}

export function buildCreateLeadApiBody(form: CreateLeadFormSnapshot): Record<string, unknown> {
  if (leadContactCreateUsesCrmContact(form)) {
    throw new Error("Use buildCreateContactApiBodyFromLeadContactForm for CRM Account attach.");
  }
  assertValidCrmChannelEmails(form.contact.channels, "Lead Contact email");
  assertValidCrmAddresses(form.contact.channels.addresses, "Lead Contact address");
  assertValidCrmChannelEmails(form.company.channels, "Lead Account email");
  assertValidCrmAddresses(form.company.channels.addresses, "Lead Account address");
  for (let i = 0; i < form.attachedContacts.length; i += 1) {
    const label =
      form.attachedContacts.length > 1 ? `Lead Contact ${i + 1} email` : "Lead Contact email";
    assertValidCrmChannelEmails(form.attachedContacts[i].channels, label);
    const addressLabel =
      form.attachedContacts.length > 1 ? `Lead Contact ${i + 1} address` : "Lead Contact address";
    assertValidCrmAddresses(form.attachedContacts[i].channels.addresses, addressLabel);
  }

  const contactChannels = contactChannelsToApiBody(form.contact.channels);
  const companyChannels = contactChannelsToApiBody(form.company.channels);

  const overflow: ChannelOverflow = {};
  if (form.leadEntry === "contact" && hasExtraChannels(form.contact.channels, contactChannels)) {
    overflow.contact = {
      emails: form.contact.channels.emails.filter((e) => e.address.trim()),
      phones: form.contact.channels.phones.filter((p) => p.number.trim()),
      socials: form.contact.channels.socials.filter((s) => s.url.trim()),
      addresses: contactChannels.addresses,
    };
  }
  if (form.leadEntry === "account" && hasExtraChannels(form.company.channels, companyChannels)) {
    overflow.company = {
      emails: form.company.channels.emails.filter((e) => e.address.trim()),
      phones: form.company.channels.phones.filter((p) => p.number.trim()),
      socials: form.company.channels.socials.filter((s) => s.url.trim()),
      addresses: companyChannels.addresses,
    };
  }

  let company: Record<string, unknown> | null = null;
  let contacts: Record<string, unknown>[] = [];

  if (form.leadEntry === "account") {
    const name = form.company.name.trim();
    if (!name) throw new Error("Lead Account name is required.");
    const location = companyLocationFromChannels(form.company.channels);
    company = {
      name,
      domain: form.company.domain.trim() || undefined,
      website: form.company.website.trim() || undefined,
      industry: form.company.industry.trim() || undefined,
      industrySegment: form.company.industrySegment.trim() || undefined,
      city: location.city,
      state: location.state,
      addresses: companyChannels.addresses,
    };
    if (companyChannels.email) {
      // Forward-compatible scalar; lead create path may not persist on LeadAccount yet.
      company.email = companyChannels.email;
    }
    if (companyChannels.phone) {
      company.companyPhone = companyChannels.phone;
    }

    const attachedOverflow: NonNullable<ChannelOverflow["attachedContacts"]> = [];
    contacts = form.attachedContacts.map((draft, index) => {
      const label = form.attachedContacts.length > 1 ? `Lead Contact ${index + 1}` : "Lead Contact";
      assertContactDraftValid(draft, label);
      const apiChannels = contactChannelsToApiBody(draft.channels);
      if (hasExtraChannels(draft.channels, apiChannels)) {
        attachedOverflow.push({
          emails: draft.channels.emails.filter((e) => e.address.trim()),
          phones: draft.channels.phones.filter((p) => p.number.trim()),
          socials: draft.channels.socials.filter((s) => s.url.trim()),
          addresses: apiChannels.addresses,
        });
      }
      return contactDraftToApiBody(draft);
    });
    if (attachedOverflow.length > 0) {
      overflow.attachedContacts = attachedOverflow;
    }
  } else {
    const hasName = Boolean(
      [form.contact.firstName.trim(), form.contact.lastName.trim()].filter(Boolean).join(" ").trim(),
    );
    const hasIdentifier = Boolean(contactChannels.email || contactChannels.linkedinUrl);
    if (!hasName || !hasIdentifier) {
      throw new Error("Lead Contact needs a name plus at least one email or social profile (e.g. LinkedIn).");
    }

    if (form.attach.mode === "existing") {
      if (!form.attach.existingAccountId) {
        throw new Error("Select a Lead Account to attach, or choose create new.");
      }
      if (crmAccountIsConverted(form.attach.existingAccountStatus)) {
        throw new Error("Converted CRM Accounts must use Contact create, not Lead Contact create.");
      }
      const attachName = form.attach.existingAccountName.trim();
      if (!attachName) throw new Error("Selected Lead Account is missing a name.");
      company = {
        name: attachName,
        domain: form.attach.existingAccountDomain.trim() || undefined,
      };
      overflow.attachAccountIds = [form.attach.existingAccountId];
    } else if (form.attach.mode === "new") {
      const attachName = form.company.name.trim();
      if (!attachName) throw new Error("Enter a name for the new Lead Account to attach.");
      const location = companyLocationFromChannels(form.company.channels);
      company = {
        name: attachName,
        domain: form.company.domain.trim() || undefined,
        website: form.company.website.trim() || undefined,
        industry: form.company.industry.trim() || undefined,
      industrySegment: form.company.industrySegment.trim() || undefined,
        city: location.city,
        state: location.state,
        addresses: companyChannels.addresses,
      };
      if (companyChannels.email) company.email = companyChannels.email;
      if (companyChannels.phone) company.companyPhone = companyChannels.phone;
    }

    contacts = [
      {
        firstName: form.contact.firstName.trim() || undefined,
        lastName: form.contact.lastName.trim() || undefined,
        title: form.contact.title.trim() || undefined,
        email: contactChannels.email || undefined,
        phone: contactChannels.phone || undefined,
        linkedinUrl: contactChannels.linkedinUrl || undefined,
        emails: contactChannels.emails,
        phones: contactChannels.phones,
        socials: contactChannels.socials,
        addresses: contactChannels.addresses,
      },
    ];
  }

  const sourceDetail = buildSourceDetail(Object.keys(overflow).length ? overflow : null);

  const ownerId = form.ownerId.trim();
  return {
    company,
    contacts,
    source: form.source,
    sourceDetail,
    status: form.status,
    stage: form.stage,
    score: 0,
    ...(ownerId ? { ownerId } : {}),
  };
}
